import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
  Bot, MessageSquare, Search, Globe, ArrowRight, Eye,
  Sparkles, Database, Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const WhatIsGEOPage = () => (
  <div className="min-h-screen pt-28 pb-20" data-testid="what-is-geo-page">
    <Helmet>
      <title>What Is GEO? Generative Engine Optimization Explained (2026) | Digital Media Ninja</title>
      <meta name="description" content="Generative Engine Optimization (GEO) is the practice of optimizing your brand to be cited by AI search engines like ChatGPT, Perplexity, and Google AI Overviews." />
      <link rel="canonical" href="https://digitalmedianinja.com/what-is-geo/" />
    </Helmet>

    <article className="container-custom max-w-4xl mx-auto px-4" data-speakable="true">
      <div className="mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm font-medium mb-6">
          <Bot className="w-4 h-4" /> Definition Guide
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">What Is Generative Engine Optimization (GEO)?</h1>
        <p className="text-lg text-gray-300 leading-relaxed mb-6" data-speakable="true">
          <strong className="text-white">Generative Engine Optimization (GEO)</strong> is the practice of optimizing your digital presence to be cited, recommended, and referenced by AI-powered search engines — including ChatGPT, Google AI Overviews, Perplexity AI, Microsoft Copilot, and Gemini. While traditional SEO focuses on ranking web pages, GEO focuses on becoming the <em>source</em> that generative AI models trust and cite when answering user queries.
        </p>
      </div>

      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">Why GEO Matters in 2026</h2>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {[
            { value: "84%", label: "of Google queries now trigger AI Overviews" },
            { value: "100M+", label: "weekly active ChatGPT users searching for businesses" },
            { value: "40%", label: "of Gen Z prefer AI search over traditional Google" },
            { value: "3x", label: "higher click-through from AI citations vs. page-2 rankings" },
          ].map((stat, i) => (
            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 text-center">
              <div className="text-2xl font-bold text-purple-400 mb-1">{stat.value}</div>
              <p className="text-gray-400 text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
        <p className="text-gray-300 leading-relaxed">
          Search behavior is shifting from clicking blue links to reading AI-generated answers. When a potential customer asks ChatGPT "best SEO agency in Texas" or Google shows an AI Overview for "how to optimize Google Business Profile," the businesses that get cited win the click — and often the customer. GEO is how you ensure your brand is one of those cited sources.
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">How GEO Works</h2>
        <div className="space-y-4">
          {[
            { icon: Database, title: "Structured Data & Schema", desc: "GEO relies heavily on structured data — Organization, Service, FAQ, Article, and SpeakableSpecification schema — to help LLMs understand your business entities, services, and expertise accurately." },
            { icon: Globe, title: "Entity Authority Building", desc: "LLMs trust brands with strong entity signals: consistent NAP data, Knowledge Graph presence, Wikipedia mentions, authoritative backlinks, and third-party citations across trusted publications." },
            { icon: Sparkles, title: "Citation-Optimized Content", desc: "Content must be written for AI citation — clear definitions, statistical claims with sources, structured comparisons, and factual prose that LLMs can extract and reference without distortion." },
            { icon: Shield, title: "RAG Pipeline Optimization", desc: "Modern LLMs use Retrieval-Augmented Generation (RAG) to fetch real-time data before generating answers. GEO ensures your content ranks in the retrieval systems that feed these AI models." },
            { icon: Eye, title: "AI Visibility Monitoring", desc: "GEO includes ongoing monitoring of how often and in what context AI models mention your brand — across ChatGPT, Perplexity, Gemini, Copilot, and Google AI Overviews." },
          ].map((item, i) => (
            <div key={i} className="flex gap-4 bg-white/5 border border-white/10 rounded-xl p-5">
              <item.icon className="w-6 h-6 text-purple-400 shrink-0 mt-1" />
              <div>
                <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">GEO vs. SEO vs. AEO</h2>
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="grid grid-cols-4 bg-white/5 px-5 py-3 border-b border-white/10">
            <span className="text-gray-400 text-sm font-medium">Aspect</span>
            <span className="text-gray-400 text-sm font-medium">SEO</span>
            <span className="text-emerald-400 text-sm font-medium">AEO</span>
            <span className="text-purple-400 text-sm font-medium">GEO</span>
          </div>
          {[
            ["Target", "Google blue links", "Featured snippets & voice", "AI-generated answers"],
            ["Metric", "Rankings & clicks", "Snippet wins & voice answers", "AI citation frequency"],
            ["Content", "Keyword pages", "Q&A pairs", "Entity-rich factual prose"],
            ["Schema", "Article, Breadcrumb", "FAQ, Speakable", "Organization, Service, all"],
            ["Timeline", "3-6 months", "2-6 weeks", "2-4 months"],
          ].map(([aspect, seo, aeo, geo], i) => (
            <div key={i} className="grid grid-cols-4 px-5 py-3 border-b border-white/5 last:border-0">
              <span className="text-white text-sm font-medium">{aspect}</span>
              <span className="text-gray-400 text-sm">{seo}</span>
              <span className="text-emerald-300 text-sm">{aeo}</span>
              <span className="text-purple-300 text-sm">{geo}</span>
            </div>
          ))}
        </div>
        <p className="text-gray-400 text-sm mt-3">
          <Link to="/aeo-vs-geo-vs-seo" className="text-[#2ABFBF] hover:underline">Read the full AEO vs GEO vs SEO comparison →</Link>
        </p>
      </section>

      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white mb-6">Frequently Asked Questions</h2>
        <Accordion type="single" collapsible className="space-y-3">
          {[
            { q: "Is GEO the same as AI SEO?", a: "GEO is a more precise term for what some marketers call 'AI SEO.' While AI SEO can mean using AI tools for traditional SEO, GEO specifically means optimizing for AI-generated search results — making your brand appear in ChatGPT, AI Overviews, Perplexity, etc." },
            { q: "Can small businesses benefit from GEO?", a: "Absolutely. Local businesses in Texas are seeing particularly strong results because AI search often recommends local providers when users ask location-specific questions. A well-optimized GBP + GEO strategy can put a small business in front of thousands of AI search queries." },
            { q: "How much does GEO cost?", a: "GEO services typically range from $1,500-$5,000/month depending on scope. Most businesses combine GEO with their existing SEO program. Contact us for a custom quote based on your industry and goals." },
          ].map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="bg-white/5 border border-white/10 rounded-xl px-5">
              <AccordionTrigger className="text-white text-left text-sm font-medium hover:no-underline">{faq.q}</AccordionTrigger>
              <AccordionContent className="text-gray-400 text-sm">{faq.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Cross-links */}
      <div className="grid sm:grid-cols-3 gap-4 mb-12">
        <Link to="/what-is-aeo" className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 hover:bg-emerald-500/10 transition-colors">
          <h3 className="text-white font-semibold text-sm mb-1">What Is AEO?</h3>
          <p className="text-gray-400 text-xs">Answer Engine Optimization</p>
        </Link>
        <Link to="/what-is-llmo" className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 hover:bg-blue-500/10 transition-colors">
          <h3 className="text-white font-semibold text-sm mb-1">What Is LLMO?</h3>
          <p className="text-gray-400 text-xs">LLM Optimization</p>
        </Link>
        <Link to="/aeo-vs-geo-vs-seo" className="bg-[#2ABFBF]/5 border border-[#2ABFBF]/20 rounded-xl p-4 hover:bg-[#2ABFBF]/10 transition-colors">
          <h3 className="text-white font-semibold text-sm mb-1">AEO vs GEO vs SEO</h3>
          <p className="text-gray-400 text-xs">Full comparison guide</p>
        </Link>
      </div>

      <div className="bg-[#111] border border-white/10 rounded-2xl p-8 text-center">
        <h2 className="text-2xl font-bold text-white mb-3">Want GEO for Your Business?</h2>
        <p className="text-gray-400 mb-6">Find out if AI search engines are citing your brand — or your competitors.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/services/geo"><Button className="bg-[#2ABFBF] hover:bg-[#1E9999] text-black font-semibold">GEO Services</Button></Link>
          <Link to="/free-ai-visibility-audit"><Button variant="outline" className="border-white/20 text-gray-300">Free AI Visibility Audit</Button></Link>
        </div>
      </div>
    </article>
  </div>
);

export default WhatIsGEOPage;
